import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Box,
  Divider,
  FormControl,
  Button,
  Chip,
  Stack,
  Container,
  Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../../../services/api';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Autocomplete,
  Checkbox,
  Datas,
  TextField,
  AprovacoesCheckbox
} from '../../../components';
import Observacoes from './Observacoes';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CSVDownload } from 'react-csv';
import { tagsRel } from './static';
import { removerQuebrasDeLinha } from '../../../utils/removerQuebraDeLinha';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}

const meses = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez'
];

const columns = [
  {
    field: "mes",
    headerName: "Mês",
    valueGetter: (value) => meses[value - 1],
    type: "string",
    width: 30,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: 'numEmpregProp',
    headerName: 'Número total de empregados próprios',
    editable: true,
    type: 'number',
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    description:
      'São todos os empregados próprios que constaram na folha ' +
      'de pagamento, por qualquer período, entre o primeiro e ' +
      'último dia do mês. Incluindo empregados em férias, ' +
      'afastados e jovens aprendizes. Excluindo-se aposentados ' +
      'por invalidez.'
  },
  {
    field: 'numDemisProp',
    headerName: 'Número total de demissões próprios',
    editable: true,
    type: 'number',
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: 'numAdmisProp',
    headerName: 'Número total de admissões próprios',
    editable: true,
    type: 'number',
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: 'numEmpregTerc',
    headerName: 'Número total de empregados terceiros',
    editable: true,
    type: 'number',
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: 'numDemisTerc',
    headerName: 'Número total de demissões terceiros',
    editable: true,
    type: 'number',
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: 'numAdmisTerc',
    headerName: 'Número total de admissões terceiros',
    editable: true,
    type: 'number',
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: "createdAt",
    headerName: "Inserido",
    type: "dateTime",
    valueGetter: (value) => value && new Date(value),
    width: 157,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: "updatedAt",
    headerName: "Atualizado",
    type: "dateTime",
    valueGetter: (value) => value && new Date(value),
    width: 157,
    sortable: false,
    disableColumnMenu: true
  }
];

const Tema00a = () => {
  let startLoading = useStoreActions(actions => actions.nav.startLoading);
  let stopLoading = useStoreActions(actions => actions.nav.stopLoading);
  let user = useStoreState(state => state.auth.user);

  const [empresas, setEmpresas] = React.useState([]);
  const [cnaes, setCNAEs] = React.useState([]);
  const [registros, setRegistros] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [preenchido, setPreenchido] = React.useState(null);
  const [csvData, setCsvData] = React.useState([]);

  const hoje = React.useMemo(() => new Date(), []);
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth() + 1;
  const incluirAnoFinal = false;

  const [anoSelecionado, setAnoSelecionado] = React.useState(incluirAnoFinal ? ano : ano -1)

  let registrosPreenchidosEsperados
  if (anoSelecionado === ano){
    const trimestreAtual = Math.ceil(mes/3)
    registrosPreenchidosEsperados = (trimestreAtual - 1) * 3 * 6
  } else {
    registrosPreenchidosEsperados = 12 * 6
  }

  const schema = yup
    .object({
      empresa: yup
        .number()
        .positive()
        .nullable()
        .label('Selecione a empresa')
        .required(),
      cnae: yup
        .number()
        .positive()
        .nullable()
        .label('Selecione o CNAE')
        .required()
    })
    .required();

  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema)
  });

  const { watch, setValue } = useFormRef;

  const loadEmpresas = async mount => {
    startLoading();
    try {
      let result = (
        await api.http.get(
          '/empresas/' + (user.roleId == 'admin' ? '?all=1' : '')
        )
      ).data;
      if (mount.isMounted) setEmpresas(result);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  const loadEmpresaCNAEs = async mount => {
    setValue('cnae', null);
    if (!empresaId) {
      setCNAEs([]);
      return;
    }
    startLoading();
    try {
      let result = (await api.http.get('/empresas/' + empresaId + '/cnaes'))
        .data;
      if (mount.isMounted) setCNAEs(result);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  const empresaId = watch('empresa');
  const cnaeId = watch('cnae');

  const onChangeAno = (event, item) => {
    setAnoSelecionado(item);
  };

  const loadRegistros = async mount => {
    startLoading();
    try {
      let url =
        '00a/registros/empresa/' +
        empresaId +
        '/cnae/' +
        cnaeId +
        '/' +
        anoSelecionado;
      let getRegistros = (await api.http.get(url)).data;
      let newRegistros = [];
      let currReg = 0;
      for (let mes = 0; mes < 12; mes++) {
        if (
          getRegistros.length > currReg &&
          getRegistros[currReg].mes == mes + 1
        ) {
          newRegistros.push(getRegistros[currReg++]);
        } else {
          newRegistros.push({
            ano: anoSelecionado,
            mes: mes + 1,
            empresaId: empresaId,
            cnaeId: cnaeId
          });
        }
      }
      if (mount.isMounted) setRegistros(newRegistros);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  const processRowUpdate = React.useCallback(async (newRow, oldRow) => {
    startLoading();

    try {
      let result;
      if (newRow.id) {
        result = (
          await api.http.put(
            '00a/registros/empresa/' +
              newRow.empresaId +
              '/cnae/' +
              newRow.cnaeId +
              '/' +
              newRow.ano +
              '/' +
              newRow.id,
            newRow
          )
        ).data;
      } else {
        result = (
          await api.http.post(
            '00a/registros/empresa/' +
              oldRow.empresaId +
              '/cnae/' +
              oldRow.cnaeId +
              '/' +
              oldRow.ano,
            newRow
          )
        ).data;
      }
      setReloadOnTabChange(true);
      stopLoading();
      return result;
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
      return oldRow;
    }
  }, []);

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    loadEmpresas(mount);
    return () => {
      mount.isMounted = false;
    };
  }, []);

  const loadPreenchido = async () => {
    startLoading();
    try {
      let result = (
        await api.http.get(
          `00a/registros-preenchidos/empresa/${empresaId}/cnae/${cnaeId}/${anoSelecionado}`
        )
      ).data;
      setPreenchido(result);
      if (result.length == 0) {
        setPreenchido(null);
      }
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    loadEmpresaCNAEs(mount);
    return () => {
      mount.isMounted = false;
    };
  }, [empresaId]);

  React.useEffect(()=>{
    if(!empresaId || !cnaeId)
      return;
    let mount = {
      isMounted: true
    };
    loadRegistros(mount);
    return () => {
      mount.isMounted = false;
    };
  }, [empresaId, cnaeId, anoSelecionado]);

  const [reloadOnTabChange, setReloadOnTabChange] = React.useState(false);
  const [tab, setTab] = React.useState(0);

  // abre o dialogo de finalizar e salvar
  const handleClickOpen = () => {
    loadPreenchido();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  function extrairDataHora(dateTimeStr) {
    const dateObj = new Date(dateTimeStr);
    const year = dateObj.getFullYear().toString();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    const date = `${day}-${month}-${year}`;
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const seconds = dateObj.getSeconds().toString().padStart(2, '0');
    const time = `${hours}:${minutes}:${seconds}`;
    return date + ' ' + time;
  }
  const exportDados = async () => {
    startLoading();
    try {
      const dados = (await api.http.get('/00a/registros')).data;
      console.log(dados);
      const dadosCsv = dados.map(data => {
        return {
          cnpj: data?.empresa?.cnpj,
          empresa: removerQuebrasDeLinha(data?.empresa?.nome),
          ano: data?.ano,
          mes: meses[data?.mes - 1],
          cnae: `${data?.cnae?.codigo} - ${data?.cnae?.descr}`,
          numEmpregProp: data?.numEmpregProp,
          numDemisProp: data?.numDemisProp,
          numAdmisProp: data?.numAdmisProp,
          numEmpregTerc: data?.numEmpregTerc,
          numDemisTerc: data?.numDemisTerc,
          numAdmisTerc: data?.numAdmisTerc,
          createdAt: data.createdAt ? extrairDataHora(data.createdAt) : null,
          updatedAt: data.updatedAt ? extrairDataHora(data.updatedAt) : null
        };
      });

      if (dadosCsv.length == 0) {
        stopLoading({
          message: 'Nenhum registro encontrado.',
          severity: 'error'
        });
      } else {
        setCsvData([tagsRel, ...dadosCsv]);
        stopLoading();
      }
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  return (
    <Container maxWidth="false">
      <Paper
        variant="outlined"
        className="center-box"
        sx={{
          textAlign: 'center',
          px: 4,
          pt: 2,
          pb: 3,
          my: 5
        }}
      >
        <Divider>
          <h3>Dados básicos</h3>
        </Divider>
        <Box component="form" noValidate autoComplete="off">
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <Autocomplete
                field="empresa"
                useFormRef={useFormRef}
                options={empresas}
                getOptionLabel={empresa =>
                  empresa.nome + ' (' + empresa.cnpj + ')'
                }
                schema={schema}
              />
            </FormControl>
            <FormControl fullWidth>
              <Autocomplete
                field="cnae"
                useFormRef={useFormRef}
                options={cnaes}
                getOptionLabel={(cnae) => cnae.codigo + " - " + cnae.descr}
                renderOption={(props, cnae) => {
                  const {key, ...restProps} = props;
                  return (
                    <Box
                      key={key}
                      component="li"
                      sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                      {...restProps}
                    >
                      {cnae.codigo} - {cnae.descr}{" "}
                    </Box>
                  );
                }}
                schema={schema}
              />
            </FormControl>
            <Datas
              onChange={onChangeAno}
              incluirAnoFinal={incluirAnoFinal}
              value={anoSelecionado}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <AprovacoesCheckbox />
          </Stack>
          {['admin', 'gestor', 'preenchedorSso'].includes(user.roleId) && (
            <Stack direction="row" spacing={2} mt={2}>
              <Button
                variant="contained"
                onClick={exportDados}
                sx={{ height: 45 }}
              >
                <FileDownloadIcon /> Exportar Dados (CSV)
              </Button>
              {csvData && csvData.length > 0 && (
                <CSVDownload
                  data={csvData}
                  headers={tagsRel.headers}
                  separator=";"
                  target="_blank"
                />
              )}
            </Stack>
          )}
        </Box>
      </Paper>

      {empresaId && cnaeId && (
        <div>
          <Paper
            variant="outlined"
            className="preenchimento-grid center-box"
            sx={{
              textAlign: 'center',
              px: 4,
              pt: 2,
              pb: 3,
              my: 5
            }}
          >
            <TabPanel value={tab} index={0}>
              <DataGrid
                rows={registros}
                columns={columns}
                autoHeight={true}
                hideFooter={true}
                getRowId={row => row.ano + '-' + row.mes}
                density="compact"
                pageSize={12}
                rowsPerPageOptions={[12]}
                disableSelectionOnClick
                processRowUpdate={processRowUpdate}
                editMode="row"
                experimentalFeatures={{ newEditingApi: true }}
                headerHeight={100}
                sx={{
                  '& .MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'break-spaces',
                    textAlign: 'left'
                  }
                }}
              />
            </TabPanel>
            <Button variant="outlined" onClick={handleClickOpen}>
              Finalizar e Enviar
            </Button>
            <Dialog
              open={open}
              onClose={handleClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {preenchido === registrosPreenchidosEsperados ? 'Sucesso!' : 'Atenção'}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  {preenchido === registrosPreenchidosEsperados
                    ? 'Agora você já pode visualizar seus dados nos gráficos dos indicadores!'
                    : `Você preencheu apenas ${preenchido} campos. Por favor, complete todos os campos para concluir, inclusive com ZERO quando aplicável.`}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} autoFocus>
                  OK
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              textAlign: 'center',
              px: 4,
              pt: 2,
              pb: 3,
              my: 5
            }}
          >
            <Divider>
              <h3>Observações</h3>
            </Divider>
            <Observacoes
              empresa={empresaId}
              cnae={cnaeId}
              ano={anoSelecionado}
            />
          </Paper>
        </div>
      )}
    </Container>
  );
};

export default Tema00a;
